import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { BonusPaymentDto, CreateBriefApplicationDto, FilterApplicationStatusDto, HireCreatorDto } from '../../common/general-types/brief-application.dto';
import { HireCreatorResponseDto } from '../../common/general-types/hire-response.dto';
import { ApplicationStatusEnum, AssignmentSummaryDto, BriefApplicationResponseDto, BriefResponseDto, RemindCreatorsResponseDto, StatusHistory, StatusHistoryGrouped } from '../../common/general-types/briefs.dto';

export type { BriefApplicationResponseDto } from '../../common/general-types/briefs.dto';
import { Observable, tap } from 'rxjs';
import { DeclineBriefApplicationDto } from '../../common/general-types/brief-application.dto';
import { CreatorResponseDto } from '../../common/general-types/creators.dto';
import { PricingModelV2Dto } from '../../common/general-types/pricing.dto';
import { PosthogService } from '../analytics/posthog.service';
import { AdConversionsService } from '../analytics/ad-conversions.service';
import { BroadcastStreamMessageDto } from '@/app/atoms/broadcast-message/broadcast-message';
import { isInsufficientFundsHireResponse } from '../../pages/manage-brief/utils/hire-insufficient-funds.util';
@Injectable({
  providedIn: 'root'
})
export class BriefApplicationService {

  currentApplicationSignal = signal<BriefApplicationResponseDto | undefined>(undefined);
  declineApplicationSignal = signal<DeclineBriefApplicationDto | null>(null);
  unHireApplicationSignal = signal<DeclineBriefApplicationDto | null>(null);


  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  private posthogService = inject(PosthogService);
  private adConversions = inject(AdConversionsService);

  createBulkBriefApplications(applications: CreateBriefApplicationDto[]) {
    const update_preferences_url = `${environment.shout_api.url}/brief-application/bulk`;
    return this.http.post(update_preferences_url, applications, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  applyforCreator(createBriefApplicationDto: CreateBriefApplicationDto): Observable<BriefApplicationResponseDto> {
    const payload = { ...createBriefApplicationDto, applicationStatus: ApplicationStatusEnum.Applied };
    const url = `${environment.shout_api.url}/brief-application`;
    return this.http.post<BriefApplicationResponseDto>(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  filterCreatorsApplications(
    creatorUserId: string,
    status: 'all' | 'invited' | 'applied' | 'hired' | 'completed',
    take = 50,
    skip = 0,
  ): Observable<BriefApplicationResponseDto[]> {
    const url = `${environment.shout_api.url}/brief-application/filter-my-applications`;
    return this.http.post<BriefApplicationResponseDto[]>(
      url,
      { creatorId: creatorUserId, status, take, skip },
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  inviteCreator(createBriefApplicationDto: CreateBriefApplicationDto): Observable<BriefApplicationResponseDto> {
    const payload = { ...createBriefApplicationDto, applicationStatus: ApplicationStatusEnum.Invited };
    const url = `${environment.shout_api.url}/brief-application`;
    return this.http.post<BriefApplicationResponseDto>(url, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  createBriefApplication(creator: CreatorResponseDto, brief: BriefResponseDto ): Observable<BriefApplicationResponseDto> {

    let application: CreateBriefApplicationDto = {
      briefId: brief.id,
      creatorUserId: creator.blazeFirebaseUid,
      firstName: creator.firstName,
      lastName: creator.lastName,
      applicationStatus: 1,
      hiredPricing: brief.basePricingModel as PricingModelV2Dto,
      createdBy: this.firebaseService.userId!
    }

    const create_brief_application_url = `${environment.shout_api.url}/brief-application`;
    return this.http.post<BriefApplicationResponseDto>(create_brief_application_url, application, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(() => {
      this.posthogService.posthog.capture('creator_invited_to_brief', {
        brief_id: brief.id,
        creator_uid: creator.blazeFirebaseUid,
      });
    }));
  }

  getCreatorRecentBrands(creatorUid: string) {
    const get_creator_recent_brands_url = `${environment.shout_api.url}/brief-application/recent-brand-collabs/${creatorUid}`;
    return this.http.get(get_creator_recent_brands_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  removeCreatorInvite(applicationId: string) {
    console.log('removeCreatorInvite() -> Access Token: ', this.firebaseService.accessToken);
    const remove_creator_invite_url = `${environment.shout_api.url}/brief-application/${applicationId}/uninvite`;
    return this.http.post(remove_creator_invite_url, null, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getAssignmentSummary(briefId: string) {
    const assignment_summary_url = `${environment.shout_api.url}/brief-application/assignment-summary/${briefId}`;
    return this.http.get<AssignmentSummaryDto>(assignment_summary_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /** Push-notifies invited creators. The API skips anyone reminded in the last 24h. */
  remindCreators(applicationIds: string[]) {
    const remind_creators_url = `${environment.shout_api.url}/brief-application/remind`;
    return this.http.post<RemindCreatorsResponseDto>(remind_creators_url, { applicationIds }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  promoteCreators(payload: any) {
    const promote_creators_url = `${environment.shout_api.url}/brief-application/promote-to-brief`;
    return this.http.post(promote_creators_url, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  hireCreator(hireCreator: HireCreatorDto) {
    const hire_creator_url = `${environment.shout_api.url}/brief-application/hire`;
    return this.http.post<HireCreatorResponseDto>(hire_creator_url, hireCreator, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap((res) => {
      if (!isInsufficientFundsHireResponse(res)) {
        void this.adConversions.trackCreatorHired(hireCreator.briefApplicationId);
        this.posthogService.posthog.capture('creator_hired', {
          brief_application_id: hireCreator.briefApplicationId,
          brief_id: hireCreator.briefId,
        });
      }
    }));
  }

  /**
   * Drop one cross-post channel the brand accepted at hire, releasing the money
   * reserved for it. The API refuses once that cross-post is live.
   */
  cancelCrossPlatform(briefApplicationId: string, socialChannel: number) {
    const cancel_cross_platform_url = `${environment.shout_api.url}/brief-application/cancel-cross-platform`;
    return this.http.post<BriefApplicationResponseDto>(
      cancel_cross_platform_url,
      { briefApplicationId, socialChannel },
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  findMyApplicationWithBrief(briefId: string, creatorId: string) {
    const application_with_brief_url = `${environment.shout_api.url}/brief-application/student-application-with-brief/${briefId}/${creatorId}`;
    return this.http.get(application_with_brief_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  updateProductSent(briefApplicationId: string) {
    const product_sent_url = `${environment.shout_api.url}/brief-application/product-sent/${briefApplicationId}`;
    return this.http.get<BriefApplicationResponseDto>(product_sent_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  adminSetApplicationStatus(briefApplicationId: string, setStatus: { status: ApplicationStatusEnum; user: string; }) {
    const admin_set_application_status_url = `${environment.shout_api.url}/brief-application/admin/set-application-status/${briefApplicationId}`;
    return this.http.post(admin_set_application_status_url, setStatus, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }


  getRecentActivityBusiness(businessAccountId: string, filter: FilterApplicationStatusDto) {
    const recent_activity_business_url = `${environment.shout_api.url}/brief-application-status/recent-activity/${businessAccountId}`;
    return this.http.post<StatusHistory[]>(recent_activity_business_url, filter, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getRecentActivityBusinessFilter(businessAccountId: string, filter: FilterApplicationStatusDto) {
    const recent_activity_business_url = `${environment.shout_api.url}/brief-application-status/recent-activity-filter/${businessAccountId}`;
    return this.http.post<StatusHistory[]>(recent_activity_business_url, filter, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getRecentActivityBusinessGrouped(businessAccountId: string, filter: FilterApplicationStatusDto) {
    const recent_activity_business_url = `${environment.shout_api.url}/brief-application-status/recent-activity-grouped/${businessAccountId}`;
    return this.http.post<StatusHistoryGrouped[]>(recent_activity_business_url, filter, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  payBonus(applicationId: string, bonus: BonusPaymentDto) {
    const pay_bonus_url = `${environment.shout_api.url}/brief-application/${applicationId}/bonus-payment`;
    return this.http.post(pay_bonus_url, bonus, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getBroadCastApplicants(briefId: string, statuses: ApplicationStatusEnum[]) {
    const get_broadcast_applicants_url = `${environment.shout_api.url}/brief-application/broadcast-applicants/${briefId}`;
    return this.http.post<any[]>(get_broadcast_applicants_url, statuses, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  broadcastStreamMessageByStatus(briefId: string, payload: BroadcastStreamMessageDto) {
    const broadcast_stream_message_url = `${environment.shout_api.url}/brief-application/admin/broadcast-stream-message/${briefId}`;
    return this.http.post(broadcast_stream_message_url, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /** The creator's in-progress work on live briefs, leaving out `excludeBriefId` (the brief being viewed). */
  findCreatorOpenApplications(creatorUid: string, excludeBriefId?: string) {
    const find_creator_open_applications_url = `${environment.shout_api.url}/brief-application/creator/${creatorUid}/open-applications`;
    return this.http.get<BriefApplicationResponseDto[]>(find_creator_open_applications_url, {
      headers: {
        'Content-Type': 'application/json',
      },
      ...(excludeBriefId ? { params: { excludeBriefId } } : {}),
    });
  }

  /** Reset Pricing Bits */

  setHiredEarningsIndividual(applicationId: string) {
    const set_hired_earnings_individual_url = `${environment.shout_api.url}/brief-application/set-hired-earnings/individual/${applicationId}`;
    return this.http.get<BriefApplicationResponseDto>(set_hired_earnings_individual_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }  
  /** Reset Pricing Bits END */
}
